import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { isReservedUsername, normalizeUsername } from '../utils/registrationValidation';
import '../styles/Auth.css';

const Register: React.FC = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [registeredUsername, setRegisteredUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useLanguage();

    useEffect(() => {
        if (authService.isAuthenticated()) {
            navigate('/usercp', { replace: true });
        }
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError(t('auth.passwordsDoNotMatch'));
            return;
        }

        if (formData.password.length < 6) {
            setError(t('auth.passwordMinLength'));
            return;
        }

        const username = normalizeUsername(formData.username);
        if (isReservedUsername(username)) {
            setError(t('auth.usernameReserved'));
            return;
        }

        setLoading(true);

        try {
            const response = await authService.register({
                ...formData,
                username,
                email: formData.email.trim(),
            });
            if (response.success) {
                setRegisteredUsername(formData.username);
                setSuccess(true);
            } else {
                setError(response.message || 'Registration failed');
            }
        } catch (err: any) {
            const apiMessage =
                err.response?.data?.message ||
                err.response?.data?.Message ||
                err.response?.data?.title;
            setError(apiMessage || t('auth.registerError'));
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="auth-page">
                <div className="auth-container auth-success">
                    <div className="auth-header">
                        <h1>{t('auth.registerSuccessTitle')}</h1>
                        <p>{t('auth.registerSuccessMessage')}</p>
                    </div>

                    <div className="success-message auth-success-box">
                        {registeredUsername && (
                            <p className="auth-success-user">
                                <strong>{registeredUsername}</strong>
                            </p>
                        )}
                        <p>{t('auth.registerSuccessDetail')}</p>
                    </div>

                    <Link to="/login" className="btn btn-primary btn-xl auth-success-btn">
                        {t('auth.login')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <h1>{t('auth.register')}</h1>
                    <p>{t('auth.registerSubtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && <div className="error-message">{error}</div>}

                    <div className="form-group">
                        <label htmlFor="username">{t('auth.username')}</label>
                        <input
                            type="text"
                            id="username"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            required
                            minLength={3}
                            maxLength={64}
                            placeholder={t('auth.chooseUsername')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">{t('auth.email')}</label>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            placeholder={t('auth.enterEmail')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">{t('auth.password')}</label>
                        <input
                            type="password"
                            id="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            minLength={6}
                            placeholder={t('auth.enterPasswordMin')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            required
                            placeholder={t('auth.confirmPasswordPlaceholder')}
                        />
                    </div>

                    <button 
                        type="submit" 
                        className={`btn btn-primary btn-xl ${loading ? 'btn-loading' : ''}`}
                        disabled={loading}
                    >
                        {loading ? t('auth.creatingAccount') : t('auth.register')}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>{t('auth.haveAccount')} <Link to="/login">{t('auth.loginHere')}</Link></p>
                </div>
            </div>
        </div>
    );
};

export default Register;
